<template>
  <div class="row">
    <div class="col-md-12">
      <p><b>Shipping</b></p>
      <tml-address-form
        ref="addressForm"
        field-name-prefix="shipping_address"
        show-name-fields
        :address="internalAddress"
        :country-options="internalCountries"
      >
        <template #extra-fields-before="{fieldNamePrefix}">
          <tml-input
            v-model="internalEmail"
            :field-name="`${fieldNamePrefix}_email`"
            placeholder="Email"
            type="email"
            :validation-rules="{required: true, email: true}"
          />
          <tml-input
            v-model="internalPhone"
            :field-name="`${fieldNamePrefix}_phone`"
            placeholder="Phone"
            type="tel"
          />
        </template>
      </tml-address-form>

      <p><b>Shipping method</b></p>

      <tml-new-form ref="shippingMethodNewForm" name="shipping-method-new-form">
        <tml-new-select
          ref="shipping"
          v-model="selectedShipping"
          name="shipping"
          label="Shipping"
          class="mb-4 mt-2"
          :options="formattedShippingOptions"
          :disabled="waitingForShippingMethods"
        >
          <template
            #options-custom="{onSelect, allItems, search, filter, select}"
          >
            <div
              v-for="option in allItems"
              :key="option.uid"
              class="option flex justify-start items-center w-full cursor-pointer"
              style="padding: 0.8em"
              @click.stop="$event => onSelect(option, select, filter, search)"
            >
              <span class="mr-2">
                {{ option.text }}
              </span>
              <span class="mr-2">
                <p v-if="parseFloat(option.price) === 0">Free</p>
                <tml-currency
                  v-else
                  :value="parseFloat(option.price)"
                  no-rounding
                />
              </span>
            </div>
          </template>
          <template #selected="{option, deselect, onDeselect, filter, search}">
            <div
              class="flex justify-start items-center w-full cursor-pointer"
              style="padding: 0.8em"
            >
              <span class="mr-2">
                {{ option.text }}
              </span>
              <span class="mr-2">
                <p v-if="parseFloat(option.price) === 0">Free</p>
                <tml-currency
                  v-else
                  :value="parseFloat(option.price)"
                  no-rounding
                />
              </span>
            </div>
          </template>
        </tml-new-select>
      </tml-new-form>

      <p
        v-if="
          summary.total > 150 &&
            (selectedShippingValue == 6 || selectedShippingValue == 28)
        "
        class="mb-4"
      >
        <small>
          <font-awesome-icon :icon="faExclamationCircle" />
          EU orders over £150 may be subject to duty charges
        </small>
      </p>

      <p><b>Order summary</b></p>

      <tml-form form-name="order-totals">
        <cart-totals
          v-if="summary"
          :summary="summary"
          small
          class="mb-4"
          :show-tax="showTax"
        />
      </tml-form>

      <tml-button
        text="Place order"
        primary
        fill
        :loading="confirming || loading"
        @click="confirmOrder"
      />
    </div>
  </div>
</template>

<script>
import {faExclamationCircle} from '@fortawesome/pro-light-svg-icons/faExclamationCircle';
import {formatUrl, getShippingEstimate} from '@teemill/common/helpers';
import CartTotals from './CartTotals.vue';
import CartAddressForm from './CartAddressForm.vue';
import {TmlAddressForm} from '@teemill/components';
import {Address} from '@teemill/common/models';
import {snackbar} from '@teemill/common/services';
import {debounce} from 'lodash';
import {captureException} from '@sentry/vue';

export default {
  name: 'CartConfirmShipping',

  components: {
    CartTotals,
    CartAddressForm,
    TmlAddressForm,
  },

  inject: ['confirm'],

  props: {
    checkoutResource: {
      type: Object,
      required: true,
    },

    preSelectedShipping: {
      type: Object,
      required: false,
    },

    showTax: {
      type: Boolean,
      default: true,
    },

    name: {
      type: String,
      required: true,
    },

    loading: Boolean,
  },

  data() {
    return {
      faExclamationCircle,

      internalShippingOptions: [],
      internalCheckoutData: {},
      internalAddress: null,
      internalEmail: null,
      internalPhone: null,
      internalCountries: [],
      internalStates: [],
      selectedShippingValue: null,
      confirming: false,
      waitingForShippingMethods: false,
      summary: {},
    };
  },

  computed: {
    /**
     * Only show store collect if shipping state is isle of wight
     */
    filteredShippingOptions() {
      return this.internalShippingOptions.filter(
        so =>
          so.value !== 8 || // Store Collect
          this.internalCheckoutData.billing_info.state ===
            'England/Isle of Wight'
      );
    },

    formattedShippingOptions() {
      return this.filteredShippingOptions.map(so => {
        so.shippingEstimate = getShippingEstimate(
          so?.earliestShippingEstimate,
          so?.latestShippingEstimate
        );

        return so;
      });
    },

    selectedShipping: {
      set(option) {
        if (option) {
          this.selectedShippingValue = option.value;
        } else {
          this.selectedShippingValue = null;
        }
      },
      get() {
        let option = this.internalShippingOptions.find(
          o => o.value === this.selectedShippingValue
        );

        if (!option) {
          option = this.internalShippingOptions[0];
        }

        return option;
      },
    },
  },

  watch: {
    selectedShippingValue: {
      immediate: true,
      handler(value) {
        if (value) {
          this.waitingForShippingMethods = true;
          this.axios
            .post(
              formatUrl(`/omnis/v3/checkout/${this.name}/update-shipping/`),
              {
                shipping_method: value,
              }
            )
            .then(({data}) => {
              if (data) {
                this.handleCheckoutResource(data);
              }

              this.waitingForShippingMethods = false;
            })
            .catch(() => {
              this.waitingForShippingMethods = false;
            });
        }
      },
    },

    'checkoutResource.shippingMethods': {
      immediate: true,
      handler(value) {
        this.internalShippingOptions = value;
      },
    },

    'checkoutResource.checkoutData': {
      immediate: true,
      handler(value) {
        this.internalCheckoutData = value;
      },
    },

    'checkoutResource.countries': {
      immediate: true,
      handler(value) {
        this.internalCountries = value;
      },
    },

    'checkoutResource.states': {
      immediate: true,
      handler(value) {
        this.internalStates = value;
      },
    },

    internalAddress: {
      handler(newVal) {
        this.updateInternalAddress(newVal);
      },
      deep: true,
    },

    internalEmail(newVal) {
      this.internalCheckoutData.billing_info.email = newVal;
    },

    internalPhone(newVal) {
      this.internalCheckoutData.billing_info.phone = newVal;
    },
  },

  created() {
    if (this.preSelectedShipping) {
      this.selectedShipping = this.preSelectedShipping;
    }

    const billingInfo = this.internalCheckoutData.billing_info;

    this.internalEmail = billingInfo.email;
    this.internalPhone = billingInfo.phone;

    this.internalAddress = Address.map({
      firstName: billingInfo.first_name,
      lastName: billingInfo.last_name,
      streetAddress: billingInfo.street_address,
      city: billingInfo.city,
      zip: billingInfo.zip,
      state: billingInfo.state,
      country: billingInfo.country,
    });
  },

  methods: {
    syncAddressToBillingInfo() {
      const address = this.internalAddress;
      const billingInfo = this.internalCheckoutData?.billing_info;

      if (!address || !billingInfo) {
        return;
      }

      billingInfo.first_name = address.firstName ?? '';
      billingInfo.last_name = address.lastName ?? '';
      billingInfo.street_address = address.streetAddress ?? '';
      billingInfo.city = address.city ?? '';
      billingInfo.zip = address.zip ?? '';
      billingInfo.country = address.country;
      billingInfo.state = address.state;
      billingInfo.email = this.internalEmail ?? billingInfo.email;
      billingInfo.phone = this.internalPhone ?? billingInfo.phone;
      billingInfo.act_as_billing_info = true;
    },

    updateInternalAddress: debounce(function update() {
      const newVal = this.internalAddress;
      const billingInfo = this.internalCheckoutData?.billing_info;

      if (!newVal || !billingInfo) {
        return;
      }

      const countryChanged = billingInfo.country !== newVal.country;
      const stateChanged = billingInfo.state !== newVal.state;

      this.syncAddressToBillingInfo();

      if (countryChanged) {
        this.updateShippingCountry(newVal.country);
      } else if (stateChanged) {
        this.updateShippingState(newVal.state);
      }
    }, 500),

    updateShippingCountry(value) {
      if (!value) {
        return;
      }

      this.internalCheckoutData.billing_info.country = value;
      this.internalCheckoutData.billing_info.state = null;

      this.updateCountryAndState({
        ...this.internalCheckoutData.billing_info,
        act_as_billing_info: true,
      });
    },

    updateCountryAndState(checkoutData) {
      this.waitingForShippingMethods = true;
      this.axios
        .post(
          formatUrl(
            `/omnis/v3/checkout/${this.name}/update-country-and-state/`
          ),
          checkoutData
        )
        .then(({data}) => {
          if (data) {
            this.handleCheckoutResource(data);
          }

          this.waitingForShippingMethods = false;
        })
        .catch(() => {
          this.waitingForShippingMethods = false;
        });
    },

    handleCheckoutResource(data) {
      this.internalCheckoutData = data.checkoutData;
      this.internalCountries = data.countries;

      this.internalStates = [];
      Object.keys(data.states).forEach(key => {
        this.internalStates.push({
          country_id: data.states[key].country_id,
          text: data.states[key].text.replace(/.*\//, ''),
          value: data.states[key].value,
        });
      });

      this.internalStates = this.internalStates.sort((a, b) =>
        a.text > b.text ? 1 : -1
      );
      this.internalShippingOptions = data.shippingMethods;

      const isAvailable = this.filteredShippingOptions.find(
        fso => fso.value === data.checkoutData.shipping_method
      );

      if (isAvailable) {
        this.selectedShippingValue = this.internalCheckoutData.shipping_method;
      } else {
        this.selectedShippingValue = this.filteredShippingOptions[0].value;
      }

      this.summary = data.summary;
      this.summary.total += this.summary.shipping;

      if (data.hasUnseenMessages) {
        this.$emit('unseen-messages');
      }
    },

    updateShippingState(value) {
      if (!value) {
        return;
      }

      this.internalCheckoutData.billing_info.state = value;

      this.updateCountryAndState({
        ...this.internalCheckoutData.billing_info,
        act_as_billing_info: true,
      });
    },

    updateCheckoutData(returnResource) {
      if (returnResource) {
        this.waitingForCheckoutData = true;
      }

      this.internalCheckoutData.return_resource = returnResource;

      this.axios
        .post(
          formatUrl(`/omnis/v3/checkout/${this.name}/update-checkout/`),
          this.internalCheckoutData.billing_info
        )
        .then(({data}) => {
          if (returnResource) {
            if (data) {
              this.handleCheckoutResource(data);

              this.waitingForCheckoutData = false;
            }
          }
        })
        .catch(() => {
          this.waitingForCheckoutData = false;
        });
    },

    async confirmOrder() {
      if (this.confirming || this.loading) {
        return;
      }

      this.confirming = true;

      try {
        this.syncAddressToBillingInfo();

        if (await this.$refs.addressForm.validate()) {
          await this.confirm(
            this.internalCheckoutData,
            this.selectedShippingValue
          );
        } else {
          this.showValidationSnackbar();
        }
      } catch (err) {
        snackbar.error();

        captureException(err);
      }

      this.confirming = false;
    },

    showValidationSnackbar() {
      snackbar.error('Sorry, some information is missing');
    },
  },
};
</script>
